import React, { useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
  Calendar, Users, TrendingUp, Shield, ArrowRight, Star,
  CheckCircle, Zap, Bell, BarChart3, Clock, FileCheck,
  ChevronRight, Play, Sparkles, GraduationCap, BookOpen,
  IndianRupee, ClipboardList, MessageSquare, Laptop
} from 'lucide-react';

const IMAGES = {
  dashboardMockup: '/assets/dashboard-mockup.png',
  calendarIllustration: '/assets/calendar-illustration.png',
  analyticsIllustration: '/assets/analytics-illustration.png',
  approvalIllustration: '/assets/approval-illustration.png',
  teamPhoto: 'https://images.pexels.com/photos/7691723/pexels-photo-7691723.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
  teacherClassroom: 'https://images.pexels.com/photos/8423011/pexels-photo-8423011.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
};

/* ─── Animated Counter ─── */
const AnimatedCounter = ({ target, suffix = '' }) => {
  const ref = useRef(null);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        let start = 0;
        const end = parseInt(target.replace(/[^0-9]/g, ''), 10);
        const duration = 1600;
        const step = (ts) => {
          if (!start) start = ts;
          const progress = Math.min((ts - start) / duration, 1);
          const eased = 1 - Math.pow(1 - progress, 3);
          node.textContent = Math.floor(eased * end).toLocaleString('en-IN') + suffix;
          if (progress < 1) requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
        observer.disconnect();
      },
      { threshold: 0.5 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [target, suffix]);
  return <span ref={ref}>0{suffix}</span>;
};

/* ─── Floating Badge (Hero) ─── */
const FloatingBadge = ({ icon: Icon, label, value, className, delay }) => (
  <div
    className={`absolute bg-white/[0.08] backdrop-blur-xl border border-white/[0.12] rounded-2xl px-4 py-3 shadow-2xl ${className}`}
    style={{ animation: `floatUp 0.8s ${delay} both, gentleFloat 4s ${delay} infinite ease-in-out` }}
  >
    <div className="flex items-center gap-3">
      <div className="h-10 w-10 rounded-xl bg-teal-500/20 flex items-center justify-center">
        <Icon className="h-5 w-5 text-teal-400" />
      </div>
      <div>
        <p className="text-white/60 text-xs">{label}</p>
        <p className="text-white font-semibold text-sm">{value}</p>
      </div>
    </div>
  </div>
);

export const Landing = () => {
  const navigate = useNavigate();

  const features = [
    {
      icon: GraduationCap,
      title: 'Student & Class Management',
      description: 'Manage admissions, class assignments, student profiles, and parent linking — all with powerful search and filters.',
      image: IMAGES.calendarIllustration,
      color: 'teal',
    },
    {
      icon: IndianRupee,
      title: 'Fee & Invoice Management',
      description: 'Create fee structures per class, auto-generate invoices, track payments, and handle concessions with one-click tools.',
      image: IMAGES.approvalIllustration,
      color: 'amber',
    },
    {
      icon: ClipboardList,
      title: 'Exams & Report Cards',
      description: 'Schedule exams, bulk-enter marks by subject, generate report cards — complete academic tracking for every student.',
      image: IMAGES.analyticsIllustration,
      color: 'rose',
    },
  ];

  const capabilities = [
    { icon: Shield, title: 'CERT-In Compliant', desc: 'Enterprise security & audit logs' },
    { icon: Bell, title: 'Smart Alerts', desc: 'Real-time push notifications' },
    { icon: Users, title: 'Multi-Role Access', desc: 'Admin, Staff, Parent, Student portals' },
    { icon: MessageSquare, title: 'Grievance System', desc: 'Parent ticket & escalation flows' },
    { icon: Calendar, title: 'Leave & Attendance', desc: 'Staff leave + student attendance' },
    { icon: Laptop, title: 'Configurable Modules', desc: 'Enable only what you need' },
  ];

  const testimonials = [
    {
      quote: "Ariskool replaced 4 different software tools for us. Fee collection, exam management, student records — it's all in one place now.",
      author: "Priya Sharma",
      role: "Principal, DPS Noida",
      rating: 5,
    },
    {
      quote: "Parents love the portal. They can check their child's marks, fee status, and raise concerns — all without calling the school office.",
      author: "Rajesh Kumar",
      role: "Director, Kendriya Vidyalaya",
      rating: 5,
    },
    {
      quote: "The configurable modules are brilliant. We started with just attendance and fees, then added exams later. Perfect for phased rollout.",
      author: "Ananya Desai",
      role: "Vice Principal, DAV School",
      rating: 5,
    },
  ];

  return (
    <div className="min-h-screen bg-white overflow-x-hidden" data-testid="landing-page">
      <style>{`
        @keyframes floatUp {
          from { opacity: 0; transform: translateY(24px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes gentleFloat {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }
        @keyframes slideIn {
          from { opacity: 0; transform: translateX(-30px); }
          to   { opacity: 1; transform: translateX(0); }
        }
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(32px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes pulse-ring {
          0% { transform: scale(0.9); opacity: 0.6; }
          100% { transform: scale(1.3); opacity: 0; }
        }
        .animate-fade-in-up { animation: fadeInUp 0.7s ease-out both; }
        .animate-slide-in { animation: slideIn 0.7s ease-out both; }
        .stagger-1 { animation-delay: 0.1s; }
        .stagger-2 { animation-delay: 0.2s; }
        .stagger-3 { animation-delay: 0.35s; }
        .stagger-4 { animation-delay: 0.5s; }
        .feature-card:hover .feature-img {
          transform: scale(1.05) translateY(-4px);
        }
        .feature-card:hover {
          border-color: rgba(20, 184, 166, 0.3);
        }
        .glow-teal { box-shadow: 0 0 60px 10px rgba(20,184,166,0.12); }
        .glow-amber { box-shadow: 0 0 60px 10px rgba(245,158,11,0.10); }
        .grain {
          background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E");
          opacity: 0.03;
        }
      `}</style>

      {/* ─── HEADER ─── */}
      <header className="sticky top-0 z-50 border-b border-white/[0.06] bg-slate-900/80 backdrop-blur-2xl" data-testid="landing-header">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group">
            <div className="h-9 w-9 rounded-lg bg-teal-500 flex items-center justify-center">
              <Sparkles className="h-5 w-5 text-white" />
            </div>
            <span className="text-xl font-bold text-white tracking-tight">Ariskool</span>
          </Link>
          <nav className="hidden md:flex items-center gap-1">
            {[
              { label: 'Features', path: '/features' },
              // { label: 'Pricing', path: '/pricing' },
              { label: 'Tutorials', path: '/tutorials' },
              { label: 'Contact', path: '/contact' },
            ].map((item) => (
              <Button
                key={item.path}
                variant="ghost"
                className="text-white/60 hover:text-white hover:bg-white/[0.06] rounded-lg text-sm"
                onClick={() => navigate(item.path)}
                data-testid={`header-${item.label.toLowerCase()}-btn`}
              >
                {item.label}
              </Button>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              className="text-white/70 hover:text-white hover:bg-white/[0.06] text-sm"
              onClick={() => navigate('/login')}
              data-testid="header-login-btn"
            >
              Log In
            </Button>
            <Button
              className="bg-teal-500 hover:bg-teal-400 text-slate-950 font-semibold rounded-full px-5 text-sm"
              onClick={() => navigate('/register')}
              data-testid="header-signup-btn"
            >
              Get Started
              <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* ─── HERO ─── */}
      <section className="relative pt-20 pb-28 px-6 overflow-hidden bg-slate-900" data-testid="hero-section">
        {/* Background Glow */}
        <div className="absolute top-[-200px] left-[20%] w-[600px] h-[600px] rounded-full bg-teal-500/[0.07] blur-[120px] pointer-events-none" />
        <div className="absolute top-[100px] right-[10%] w-[400px] h-[400px] rounded-full bg-amber-500/[0.05] blur-[100px] pointer-events-none" />
        <div className="absolute inset-0 grain pointer-events-none" />

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left */}
            <div>
              <div className="animate-fade-in-up inline-flex items-center gap-2 bg-teal-500/10 border border-teal-500/20 text-teal-400 px-4 py-2 rounded-full text-sm font-medium mb-8">
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-teal-400" style={{ animation: 'pulse-ring 1.5s infinite' }} />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-teal-400" />
                </span>
                Trusted by 10,000+ Indian Schools
              </div>
              <h1
                className="animate-fade-in-up stagger-1 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.1] text-white mb-6"
                data-testid="hero-title"
              >
                The complete ERP
                <br />
                <span className="bg-gradient-to-r from-teal-400 to-emerald-400 bg-clip-text text-transparent">
                  for modern schools
                </span>
              </h1>
              <p
                className="animate-fade-in-up stagger-2 text-base sm:text-lg leading-relaxed text-white/50 max-w-lg mb-10"
                data-testid="hero-description"
              >
                Students, fees, exams, attendance, grievances, leave — manage your entire school from one powerful platform. Built for CBSE, ICSE, State Boards and every school in India.
              </p>
              <div className="animate-fade-in-up stagger-3 flex flex-wrap items-center gap-4">
                <Button
                  size="lg"
                  className="h-13 px-8 rounded-full bg-teal-500 hover:bg-teal-400 text-slate-950 font-semibold text-base"
                  onClick={() => navigate('/register')}
                  data-testid="hero-cta-btn"
                >
                  Start Free Trial
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="h-13 px-8 rounded-full border-white/[0.15] text-white hover:bg-white/[0.06] text-base"
                  onClick={() => navigate('/tutorials')}
                  data-testid="hero-demo-btn"
                >
                  <Play className="mr-2 h-4 w-4" />
                  Watch Demo
                </Button>
              </div>
              <div className="animate-fade-in-up stagger-4 flex items-center gap-6 mt-8 text-sm text-white/40">
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-teal-500" />
                  Free 7-day trial
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-teal-500" />
                  No credit card needed
                </div>
              </div>
            </div>

            {/* Right — Dashboard Mockup + Floating Badges */}
            <div className="relative animate-fade-in-up stagger-2">
              <div className="relative rounded-2xl overflow-hidden border border-white/[0.08] glow-teal">
                <img
                  src={IMAGES.dashboardMockup}
                  alt="Ariskool Dashboard Preview"
                  className="w-full object-cover"
                  loading="eager"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/60 via-transparent to-transparent" />
              </div>
              <FloatingBadge icon={GraduationCap} label="Enrolled" value="2,450 Students" className="top-4 -left-6 hidden lg:flex" delay="0.6s" />
              <FloatingBadge icon={IndianRupee} label="Collected" value="98% Fees" className="bottom-20 -right-4 hidden lg:flex" delay="0.9s" />
              <FloatingBadge icon={ClipboardList} label="Exams" value="Results Live" className="bottom-4 -left-4 hidden lg:flex" delay="1.2s" />
            </div>
          </div>
        </div>
      </section>

      {/* ─── TRUST BAR ─── */}
      <section className="py-10 border-y border-slate-100 bg-slate-50/50" data-testid="trust-bar">
        <div className="max-w-5xl mx-auto px-6">
          <p className="text-center text-xs uppercase tracking-widest text-slate-400 mb-6">Compatible with all Indian school boards</p>
          <div className="flex flex-wrap items-center justify-center gap-x-12 gap-y-4 text-slate-300 text-sm font-semibold">
            {['CBSE', 'ICSE', 'State Board', 'IB', 'IGCSE', 'NIOS'].map((board) => (
              <span key={board} className="hover:text-slate-500 transition-colors cursor-default tracking-wider">{board}</span>
            ))}
          </div>
        </div>
      </section>

      {/* ─── FEATURES (Illustration Cards) ─── */}
      <section className="py-28 px-6 bg-white" data-testid="features-section">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-2xl mb-16">
            <p className="text-teal-600 text-sm font-semibold uppercase tracking-wider mb-3">Core Features</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4" data-testid="features-title">
              Everything your school needs, unified
            </h2>
            <p className="text-base text-slate-500">
              From student enrollment to report cards — a complete ERP built specifically for Indian educational institutions.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {features.map((f, idx) => {
              const Icon = f.icon;
              return (
                <div
                  key={idx}
                  className="feature-card group bg-white border border-slate-200 rounded-2xl overflow-hidden transition-all duration-500 hover:shadow-xl hover:border-teal-200"
                  data-testid={`feature-card-${idx}`}
                >
                  <div className="h-56 overflow-hidden bg-gradient-to-b from-slate-50 to-white flex items-center justify-center p-6">
                    <img
                      src={f.image}
                      alt={f.title}
                      className="feature-img h-full w-auto object-contain transition-transform duration-500"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-6 pt-2">
                    <div className="flex items-center gap-3 mb-3">
                      <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${
                        f.color === 'teal' ? 'bg-teal-100' :
                        f.color === 'amber' ? 'bg-amber-100' : 'bg-rose-100'
                      }`}>
                        <Icon className={`h-4 w-4 ${
                          f.color === 'teal' ? 'text-teal-600' :
                          f.color === 'amber' ? 'text-amber-600' : 'text-rose-600'
                        }`} />
                      </div>
                      <h3 className="text-lg font-semibold text-slate-900">{f.title}</h3>
                    </div>
                    <p className="text-sm text-slate-500 leading-relaxed">{f.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─── CAPABILITIES GRID ─── */}
      <section className="py-24 px-6 bg-white" data-testid="capabilities-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-teal-600 text-sm font-semibold uppercase tracking-wider mb-3">Why Ariskool</p>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
                Built for the unique needs of K-12 schools
              </h2>
              <p className="text-base text-slate-500 mb-10">
                We understand the complexity of managing students, staff, parents, and compliance across multiple boards and regulatory requirements.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {capabilities.map((cap, idx) => {
                  const Icon = cap.icon;
                  return (
                    <div
                      key={idx}
                      className="flex items-start gap-3 p-4 rounded-xl bg-slate-50 border border-slate-100 hover:border-teal-200 hover:bg-teal-50/50 transition-colors duration-300"
                    >
                      <div className="h-9 w-9 rounded-lg bg-teal-100 flex items-center justify-center flex-shrink-0">
                        <Icon className="h-4 w-4 text-teal-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-slate-900 text-sm">{cap.title}</p>
                        <p className="text-xs text-slate-500">{cap.desc}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="relative">
              <div className="rounded-2xl overflow-hidden shadow-2xl shadow-slate-200/50">
                <img
                  src={IMAGES.teacherClassroom}
                  alt="Teacher in modern Indian classroom"
                  className="w-full h-[480px] object-cover"
                  loading="lazy"
                />
              </div>
              {/* Overlay stat card */}
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl border border-slate-100 p-5 max-w-[220px]">
                <div className="flex items-center gap-3 mb-2">
                  <div className="h-10 w-10 rounded-full bg-emerald-100 flex items-center justify-center">
                    <TrendingUp className="h-5 w-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">93%</p>
                    <p className="text-xs text-slate-500">Less paperwork</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── HOW IT WORKS ─── */}
      <section className="py-28 px-6 bg-slate-50" data-testid="how-it-works">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-20">
            <p className="text-teal-600 text-sm font-semibold uppercase tracking-wider mb-3">How It Works</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">Go live in three steps</h2>
            <p className="text-base text-slate-500 max-w-xl mx-auto">
              Set up your school in minutes, not days. No complex IT requirements.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative">
            {/* Connector line (desktop) */}
            <div className="hidden md:block absolute top-12 left-[17%] right-[17%] h-[2px] bg-gradient-to-r from-teal-200 via-teal-300 to-teal-200" />
            {[
              { step: '01', title: 'Register Your School', desc: 'Create your account with school details. Get a unique School Code in under 2 minutes.', color: 'teal' },
              { step: '02', title: 'Configure Modules', desc: 'Enable only the ERP modules you need — Fees, Exams, Attendance, Grievances, and more.', color: 'emerald' },
              { step: '03', title: 'Go Live', desc: 'Add students, staff, and parents. Start managing your entire school from day one.', color: 'cyan' },
            ].map((item, idx) => (
              <div key={idx} className="relative text-center group">
                <div className="relative z-10 h-24 w-24 rounded-2xl bg-white shadow-lg border border-slate-200 flex items-center justify-center mx-auto mb-6 group-hover:shadow-xl group-hover:border-teal-300 transition-all duration-300">
                  <span className="text-3xl font-black text-teal-500">{item.step}</span>
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">{item.title}</h3>
                <p className="text-sm text-slate-500 max-w-xs mx-auto">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── STATS ─── */}
      <section className="py-20 px-6 bg-teal-600" data-testid="stats-section">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { target: '10000', suffix: '+', label: 'Schools Across India' },
              { target: '1000000', suffix: '+', label: 'Active Users' },
              { target: '99', suffix: '.9%', label: 'Uptime SLA' },
              { target: '15', suffix: 'hrs', label: 'Saved / Week' },
            ].map((stat, idx) => (
              <div key={idx} className="text-center">
                <div className="text-4xl md:text-5xl font-extrabold text-white mb-2">
                  <AnimatedCounter target={stat.target} suffix={stat.suffix} />
                </div>
                <div className="text-sm text-teal-100">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── PRODUCT SHOWCASE ─── */}
      <section className="py-28 px-6 bg-white" data-testid="showcase-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <p className="text-teal-600 text-sm font-semibold uppercase tracking-wider mb-3">Product Preview</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              A platform your entire school will love
            </h2>
            <p className="text-base text-slate-500 max-w-xl mx-auto">
              Clean dashboards for admins, intuitive portals for parents and students — from fee tracking to exam analytics.
            </p>
          </div>
          <div className="relative rounded-2xl overflow-hidden border border-slate-200 shadow-2xl shadow-slate-200/60">
            <img
              src={IMAGES.dashboardMockup}
              alt="Ariskool Platform Dashboard"
              className="w-full"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-white via-transparent to-transparent" />
            <div className="absolute bottom-8 left-1/2 -translate-x-1/2">
              <Button
                className="bg-teal-500 hover:bg-teal-400 text-slate-950 font-semibold rounded-full px-8 shadow-xl"
                onClick={() => navigate('/register')}
                data-testid="showcase-cta-btn"
              >
                Try it yourself
                <ChevronRight className="ml-1 h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* ─── TESTIMONIALS ─── */}
      <section className="py-28 px-6 bg-slate-50" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-teal-600 text-sm font-semibold uppercase tracking-wider mb-3">Testimonials</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">Loved by schools across India</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((t, idx) => (
              <div
                key={idx}
                className="bg-white rounded-2xl border border-slate-200 p-8 hover:shadow-xl hover:border-slate-300 transition-all duration-300 flex flex-col"
                data-testid={`testimonial-${idx}`}
              >
                <div className="flex items-center gap-1 mb-5">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <Star key={s} className="h-4 w-4 fill-amber-400 text-amber-400" />
                  ))}
                </div>
                <p className="text-slate-700 leading-relaxed mb-6 flex-1">"{t.quote}"</p>
                <div className="flex items-center gap-3 pt-4 border-t border-slate-100">
                  <div className="h-10 w-10 rounded-full bg-gradient-to-br from-teal-400 to-emerald-500 flex items-center justify-center text-white font-bold text-sm">
                    {t.author.split(' ').map((n) => n[0]).join('')}
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900 text-sm">{t.author}</p>
                    <p className="text-xs text-slate-500">{t.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── CTA ─── */}
      <section className="relative py-28 px-6 bg-slate-900 overflow-hidden" data-testid="cta-section">
        <div className="absolute top-[-100px] left-1/2 -translate-x-1/2 w-[800px] h-[400px] rounded-full bg-teal-500/[0.1] blur-[120px] pointer-events-none" />
        <div className="max-w-3xl mx-auto text-center relative z-10">
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white mb-6 leading-tight"
            data-testid="cta-title"
          >
            Ready to modernize your school?
          </h2>
          <p className="text-base sm:text-lg text-slate-400 mb-10 max-w-lg mx-auto" data-testid="cta-description">
            Join thousands of Indian schools managing everything from one platform. Plans start at just <span className="text-teal-400 font-semibold">&#8377;1,999/month</span>.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <Button
              size="lg"
              className="h-14 px-10 rounded-full bg-teal-500 hover:bg-teal-400 text-white font-semibold text-base"
              onClick={() => navigate('/register')}
              data-testid="cta-btn"
            >
              Get Started for Free
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="h-14 px-10 rounded-full border-white/20 text-white hover:bg-white/[0.06] text-base"
              onClick={() => navigate('/pricing')}
            >
              View Pricing
            </Button>
          </div>
          <p className="mt-6 text-xs text-slate-500">No credit card required. Cancel anytime.</p>
        </div>
      </section>

      {/* ─── FOOTER ─── */}
      <footer className="border-t border-slate-200 py-16 px-6 bg-slate-50" data-testid="footer">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-8 mb-12">
            <div className="col-span-2 md:col-span-1">
              <Link to="/" className="flex items-center gap-2 mb-4">
                <div className="h-8 w-8 rounded-lg bg-teal-500 flex items-center justify-center">
                  <Sparkles className="h-4 w-4 text-white" />
                </div>
                <span className="text-lg font-bold text-slate-900">Ariskool</span>
              </Link>
              <p className="text-sm text-slate-500 leading-relaxed">
                Complete School ERP for Indian K-12 institutions.
              </p>
            </div>
            {[
              {
                title: 'Product',
                links: [
                  { label: 'Features', to: '/features' },
                  { label: 'Pricing', to: '/pricing' },
                  { label: 'Tutorials', to: '/tutorials' },
                ],
              },
              {
                title: 'Company',
                links: [
                  { label: 'About', to: '/about' },
                  { label: 'Contact', to: '/contact' },
                ],
              },
              {
                title: 'Legal',
                links: [
                  { label: 'Privacy', to: '/privacy' },
                  { label: 'Terms', to: '/terms' },
                  { label: 'Security', to: '/security' },
                ],
              },
            ].map((col) => (
              <div key={col.title}>
                <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-4">{col.title}</h4>
                <ul className="space-y-2">
                  {col.links.map((link) => (
                    <li key={link.to}>
                      <Link to={link.to} className="text-sm text-slate-500 hover:text-teal-600 transition-colors">
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
            <div>
              <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-4">Resources</h4>
              <ul className="space-y-2">
                <li><Link to="/security" className="text-sm text-slate-500 hover:text-teal-600 transition-colors">Security</Link></li>
                <li><Link to="/rti" className="text-sm text-slate-500 hover:text-teal-600 transition-colors">RTI Disclosure</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-200 pt-8 flex flex-col sm:flex-row items-center justify-between gap-2 text-sm text-slate-400">
            <span>&copy; 2026 Ariskool. All rights reserved.</span>
            <span>
              A product of{' '}
              <a href="https://arismeta.com" target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:text-teal-700 transition-colors font-semibold">
                Arismeta
              </a>
            </span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
